import { useMemo } from "react";
import { View, StyleSheet, useWindowDimensions } from "react-native";
import Animated, { LinearTransition } from "react-native-reanimated";
import {
  Baby,
  CalendarDays,
  Check,
  Heart,
  MapPin,
  Mars,
  Snowflake,
  Telescope,
  Venus,
} from "lucide-react-native";
import { useOnboarding } from "../OnboardingContext";
import { deriveChips } from "../utils/deriveChips";
import { useTranslations } from "@src/packages/i18n";
import type { OnboardingData } from "../types";
import AnswerChip from "./AnswerChip";
import { getOnboardingViewport } from "../onboardingViewport";
import { BackLink } from "@louise/moon-ui";

type Props = {
  mode: "intro" | "stepper";
  canGoBack: boolean;
  onBack: () => void;
  onChipPress?: (screenName: string) => void;
  currentRoute?: string;
};

/** Fallback order used before the visitor selects a primary goal. */
const DEFAULT_SCREEN_ORDER = [
  "WelcomeGoals",
  "FamilyBuildingPath",
  "JourneyStage",
  "Sex",
  "Birthday",
  "Location",
];

function getScreenOrder(primaryIntent: OnboardingData["primaryIntent"]) {
  if (primaryIntent === "grow") {
    return [
      "WelcomeGoals",
      "FamilyBuildingPath",
      "Sex",
      "JourneyStage",
      "Birthday",
      "Location",
    ];
  }
  if (primaryIntent === "preserve") {
    return ["WelcomeGoals", "JourneyStage", "Sex", "Birthday", "Location"];
  }
  if (primaryIntent === "donate") {
    return ["WelcomeGoals", "HowAreYouHelping", "Birthday", "Location"];
  }
  if (primaryIntent === "learn") {
    return ["WelcomeGoals", "Birthday", "Location"];
  }
  return DEFAULT_SCREEN_ORDER;
}

/** Map chip keys to the screen where that data is entered */
const CHIP_SCREEN_MAP: Record<string, string> = {
  intent: "WelcomeGoals",
  partner: "FamilyBuildingPath",
  sex: "Sex",
  age: "Birthday",
  location: "Location",
};

function chipKeyToScreen(key: string): string | null {
  if (CHIP_SCREEN_MAP[key]) return CHIP_SCREEN_MAP[key]!;
  return null;
}

function chipIcon(key: string, data: OnboardingData) {
  if (key === "intent") {
    if (data.primaryIntent === "preserve") return Snowflake;
    if (data.primaryIntent === "grow") return Baby;
    if (data.primaryIntent === "donate") return Heart;
    if (data.primaryIntent === "learn") return Telescope;
  }
  if (key === "sex") return data.gender === "AMAB" ? Mars : Venus;
  if (key === "age") return CalendarDays;
  if (key === "location") return MapPin;
  return Check;
}

export default function AnswerChipBar({
  mode,
  canGoBack,
  onBack,
  onChipPress,
  currentRoute,
}: Props) {
  const { width, height } = useWindowDimensions();
  const { compact, condensed } = getOnboardingViewport(width, height);
  const t = useTranslations();
  const { data } = useOnboarding();
  const screenOrder = getScreenOrder(data.primaryIntent);
  const chips = useMemo(() => {
    const all = deriveChips(data, t);
    if (!currentRoute) return all;
    const currentIdx = screenOrder.indexOf(currentRoute);
    if (currentIdx < 0) return all;
    // Only show chips from screens before the current one
    return all.filter((chip) => {
      const screen = chipKeyToScreen(chip.key);
      if (!screen) return true;
      const screenIdx = screenOrder.indexOf(screen);
      return screenIdx >= 0 && screenIdx < currentIdx;
    });
  }, [data, currentRoute, screenOrder, t]);

  if (chips.length === 0 && mode === "intro" && !canGoBack) return null;

  return (
    <Animated.View
      layout={
        mode === "intro" && canGoBack
          ? undefined
          : LinearTransition.duration(200)
      }
      style={[
        styles.container,
        mode === "stepper" && {
          paddingTop: condensed ? 12 : 16,
        },
        mode === "intro" &&
          canGoBack && {
            ...styles.introBackContainer,
            top: compact ? 42 : condensed ? 52 : 62,
          },
      ]}
    >
      {/* Header row: back (left) + chips (right) */}
      {(mode === "stepper" || canGoBack) && (
        <View
          style={{
            flexDirection: "row",
            alignItems: "flex-start",
            paddingVertical: 6,
            marginBottom: 4,
          }}
        >
          {canGoBack && (
            <BackLink
              label={t("onboarding.back")}
              action="onboarding.back"
              tone="onDark"
              onPress={onBack}
              style={{ paddingTop: 4 }}
            />
          )}
          <View style={{ flex: 1 }} />
          {chips.length > 0 && (
            <Animated.View
              layout={LinearTransition.duration(200)}
              style={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "flex-end",
                flexShrink: 1,
                maxWidth: "70%",
              }}
            >
              {chips.map((chip, i) => {
                const screen = chipKeyToScreen(chip.key);
                return (
                  <View
                    key={chip.key}
                    style={{
                      marginLeft: i === 0 ? 0 : -7,
                      zIndex: i + 1,
                    }}
                  >
                    <AnswerChip
                      label={chip.label}
                      index={i}
                      Icon={chipIcon(chip.key, data)}
                      onPress={
                        screen && onChipPress
                          ? () => onChipPress(screen)
                          : undefined
                      }
                    />
                  </View>
                );
              })}
            </Animated.View>
          )}
        </View>
      )}

      {/* Chips centered on intro screens */}
      {chips.length > 0 && mode === "intro" && (
        <Animated.View
          layout={LinearTransition.duration(200)}
          style={{
            flexDirection: "row",
            flexWrap: "wrap",
            gap: 6,
            justifyContent: "center",
          }}
        >
          {chips.map((chip, i) => {
            const screen = chipKeyToScreen(chip.key);
            return (
              <AnswerChip
                key={chip.key}
                label={chip.label}
                index={i}
                Icon={chipIcon(chip.key, data)}
                onPress={
                  screen && onChipPress ? () => onChipPress(screen) : undefined
                }
              />
            );
          })}
        </Animated.View>
      )}
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 24,
  },
  introBackContainer: {
    position: "absolute",
    left: 0,
    right: 0,
    width: "100%",
    maxWidth: 480,
    alignSelf: "center",
    zIndex: 30,
  },
});
